import { useEffect, useRef, useState } from "react";
import { ArrowUp, Bot, FlaskConical, GitFork, Loader2, RotateCcw, Sparkles, User, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ModelMultiSelect } from "./ModelMultiSelect";
import { callEnsemble } from "@/lib/ensemble";
import { toast } from "sonner";
import { MODEL_BY_ID, modelBadgeStyle } from "@/lib/models";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useDeepResearch } from "@/hooks/useDeepResearch";
import { ResearchDossier } from "./research/ResearchDossier";
import { SEED_DEMO_QUERY } from "@/lib/deep-research/config";
import { useBackendStatus } from "@/hooks/useBackendStatus";

type Role = "user" | "assistant";
interface Msg {
  id: string;
  role: Role;
  content: string;
  models?: string[];
  perModel?: { modelId: string; content: string; ms: number; ok: boolean }[];
  voting?: { winnerIndex: number; reason: string } | null;
  ms?: number;
}

export function NeuralChat() {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [models, setModels] = useState<string[]>(["gemini-3-flash", "gpt-5.4"]);
  const [ensemble, setEnsemble] = useState(true);
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  // Carrega ou cria sessão "Default" do usuário
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: existing } = await supabase
        .from("chat_sessions").select("id").eq("user_id", user.id).eq("title", "Default").maybeSingle();
      let sid = existing?.id;
      if (!sid) {
        const { data: created } = await supabase
          .from("chat_sessions").insert({ user_id: user.id, title: "Default" }).select("id").single();
        sid = created?.id;
      }
      if (!sid) return;
      setSessionId(sid);

      const { data: msgs } = await supabase
        .from("chat_messages").select("*").eq("session_id", sid).order("created_at", { ascending: true }).limit(200);
      setMessages(((msgs as any[]) ?? []).map((m) => ({
        id: m.id, role: m.role as Role, content: m.content,
        models: (m.models as string[]) ?? undefined,
        perModel: (m.results as any) ?? undefined,
        voting: (m.voting as any) ?? null,
      })));
    })();
  }, [user]);

  // Realtime: novas mensagens (inclusive vindas do scheduler)
  useEffect(() => {
    if (!user || !sessionId) return;
    const ch = supabase
      .channel(`chat_msgs_${sessionId}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages", filter: `session_id=eq.${sessionId}` },
        (payload) => {
          const m: any = payload.new;
          setMessages((prev) => prev.find((x) => x.id === m.id) ? prev : [...prev, {
            id: m.id, role: m.role, content: m.content,
            models: m.models ?? undefined, perModel: m.results ?? undefined, voting: m.voting ?? null,
          }]);
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user, sessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    const onVoice = (e: any) => setInput((v) => (v ? v + " " : "") + e.detail);
    window.addEventListener("neural-voice", onVoice as any);
    return () => window.removeEventListener("neural-voice", onVoice as any);
  }, []);

  const dr = useDeepResearch();
  const backend = useBackendStatus();

  const send = async () => {
    // Deep Research path: substitutes the entire flow when toggle is on
    if (dr.enabled) {
      // Read the CURRENT textarea value at click time. Never silently fall back
      // to the demo query when the user has typed something — that caused the
      // dossier to show the demo query instead of the user's real one.
      const raw = input.trim();
      if (!raw) return toast.error("Digite uma query para iniciar a pesquisa.");
      if (raw.length < 10) return toast.error("Query muito curta (mín. 10 caracteres).");
      if (raw.length > 2000) return toast.error("Query muito longa (máx. 2000 caracteres).");
      setInput("");
      try {
        await dr.start(raw);
      } catch (e) {
        toast.error("Deep Research failed");
      }
      return;
    }
    if (!input.trim() || loading) return;
    if (!user || !sessionId) return toast.error("Faça login");
    if (models.length === 0) return toast.error("Selecione ao menos 1 modelo");

    const prompt = input;
    setInput("");
    setLoading(true);

    const userRow = await supabase.from("chat_messages").insert({
      session_id: sessionId, user_id: user.id, role: "user", content: prompt, models,
    }).select().single();
    if (userRow.data) {
      setMessages((m) => [...m, { id: userRow.data.id, role: "user", content: prompt }]);
    }

    try {
      const res = await callEnsemble({ prompt, models, ensemble });
      const content = res.winner?.content || "(empty)";
      const perModel = res.results.map((r) => ({ modelId: r.modelId, content: r.content, ms: r.ms, ok: r.ok }));

      const asst = await supabase.from("chat_messages").insert({
        session_id: sessionId, user_id: user.id, role: "assistant", content, models,
        results: perModel as any, voting: (res.voting as any) ?? null,
      }).select().single();

      if (asst.data) {
        setMessages((m) => [...m, {
          id: asst.data.id, role: "assistant", content, models, perModel,
          voting: res.voting ?? null, ms: res.totalMs,
        }]);
      }
    } catch (e: unknown) {
      const { notifyError } = await import("@/lib/errorToast");
      notifyError(e as Error, "Ensemble failed");
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: "⚠️ Ensemble call failed." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
        {messages.length === 0 && !loading && (
          <div className="text-center text-sm text-muted-foreground mt-12">
            <Sparkles className="w-8 h-8 mx-auto text-primary mb-2 animate-pulse" />
            Selecione 1+ modelos, mande um prompt, veja o ensemble votar.
          </div>
        )}
        {messages.map((m) => (
          <Bubble key={m.id} msg={m} />
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-primary">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="font-mono text-xs">running {models.length} model{models.length > 1 ? "s" : ""} in parallel...</span>
          </div>
        )}
        {dr.isRunning && (
          <div className="flex items-center gap-2 text-sm text-secondary">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="font-mono text-xs">
              deep research · {dr.status} · conf {Math.round(dr.metrics.confidence * 100)}% · ${dr.metrics.costUsd.toFixed(3)}
            </span>
          </div>
        )}
        {dr.dossier && <ResearchDossier dossier={dr.dossier} />}
        <div ref={endRef} />
      </div>

      <div className="px-6 pb-3 relative flex items-center gap-2 flex-wrap">
        <ModelMultiSelect
          selected={models}
          onChange={setModels}
          ensemble={ensemble}
          onToggleEnsemble={() => setEnsemble((v) => !v)}
        />
        <button
          onClick={() => dr.setEnabled(!dr.enabled)}
          className={cn(
            "text-[10px] font-mono px-2 py-0.5 rounded border flex items-center gap-1 transition-colors",
            dr.enabled
              ? "border-secondary/60 bg-secondary/15 text-secondary shadow-glow-violet"
              : "border-border text-muted-foreground hover:text-secondary hover:border-secondary/40",
          )}
          title="Substitute the chat flow by a hierarchical planner + sub-agent swarm + adversarial critic + cited dossier."
        >
          <FlaskConical className="w-3 h-3" /> {dr.enabled ? "Deep Research ON" : "Deep Research"}
        </button>
        {dr.enabled && backend.status && (
          <span
            className={cn(
              "text-[10px] font-mono px-2 py-0.5 rounded border flex items-center gap-1",
              backend.status.level === "LIVE" && "border-success/60 bg-success/15 text-success",
              backend.status.level === "DEGRADED" && "border-warning/60 bg-warning/15 text-warning",
              backend.status.level === "MOCK" && "border-destructive/60 bg-destructive/15 text-destructive",
            )}
            title={`LLMs: ${backend.status.llm.live.join(",") || "none"} · Search: ${backend.status.search.live.join(",") || "none"} · Embed: ${backend.status.embed.live.join(",") || "none"}`}
          >
            ● {backend.status.level}
          </span>
        )}
      </div>

      <div className="px-6 pb-6">
        <div className={cn("glass rounded-xl p-3", dr.enabled ? "border border-secondary/40 shadow-glow-violet" : "neural-border")}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={
              dr.enabled
                ? `Deep Research mode — type a query (10–2000 chars). Example: "${SEED_DEMO_QUERY.slice(0, 60)}…"`
                : (user ? "Command P2HM Agents... (Shift+Enter for newline · ⌘K palette)" : "Faça login para conversar...")
            }
            rows={2}
            disabled={!dr.enabled && !user}
            className="w-full bg-transparent resize-none outline-none text-sm placeholder:text-muted-foreground disabled:opacity-50"
          />
          <div className="flex items-center justify-between mt-2">
            <div className="text-[10px] font-mono text-muted-foreground">
              {dr.enabled ? (
                <>
                  <span className="text-secondary">●</span> deep research · planner+swarm+critic+synth · live audit
                  {" · "}
                  <span className={cn(input.length > 2000 && "text-destructive", input.length < 10 && input.length > 0 && "text-warning")}>
                    {input.length}/2000 chars
                  </span>
                  {" · est. "}
                  <span className="text-secondary">${(0.0035 * 50 + 8 * 0.002).toFixed(3)}</span>
                </>
              ) : (
                <>
                  <span className="text-primary">●</span> {models.length} model{models.length > 1 ? "s" : ""} ·{" "}
                  <span className="text-secondary">●</span> {ensemble ? "ensemble + judge" : "single best"} · persistido
                </>
              )}
            </div>
            <Button
              onClick={send}
              size="sm"
              disabled={
                dr.isRunning ||
                (dr.enabled
                  ? (input.trim().length > 0 && (input.trim().length < 10 || input.trim().length > 2000))
                  : (loading || !user))
              }
              className={cn(
                "h-8 gap-1.5 border-0 text-primary-foreground",
                dr.enabled
                  ? "bg-gradient-to-r from-secondary to-accent shadow-glow-violet"
                  : "bg-gradient-neural shadow-glow-cyan",
              )}
            >
              {dr.enabled ? <FlaskConical className="w-3.5 h-3.5" /> : <ArrowUp className="w-3.5 h-3.5" />}
              {dr.enabled ? (dr.isRunning ? "Researching…" : "Research") : "Send"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({ msg }: { msg: Msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={cn("flex gap-3 animate-fade-in", isUser && "flex-row-reverse")}>
      <div
        className={cn(
          "shrink-0 w-8 h-8 rounded-lg flex items-center justify-center border",
          isUser ? "bg-surface-3 border-border" : "bg-gradient-neural border-primary/40 shadow-glow-cyan",
        )}
      >
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4 text-primary-foreground" />}
      </div>
      <div className={cn("flex-1 max-w-3xl", isUser && "flex flex-col items-end")}>
        <div
          className={cn(
            "rounded-xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap",
            isUser ? "bg-surface-2 border border-border" : "glass border-primary/20",
          )}
        >
          {msg.content}
        </div>

        {!isUser && msg.perModel && msg.perModel.length > 0 && (
          <div className="mt-2 rounded-lg border border-secondary/20 bg-secondary/5 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-wider text-secondary flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Ensemble · {msg.perModel.length} models {msg.ms ? `· ${msg.ms}ms` : ""}
              </div>
              {msg.voting && (
                <span className="text-[10px] font-mono text-success">
                  judge: candidate #{msg.voting.winnerIndex + 1}
                </span>
              )}
            </div>
            <div className="space-y-1.5">
              {msg.perModel.map((r, i) => {
                const m = MODEL_BY_ID[r.modelId];
                return (
                  <div key={i} className="text-[11px] font-mono flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded border" style={modelBadgeStyle(r.modelId)}>
                      {m?.label ?? r.modelId}
                    </span>
                    <span className="text-muted-foreground truncate flex-1">
                      {r.ok ? r.content.slice(0, 80).replace(/\n/g, " ") : "⚠️ failed"}
                    </span>
                    <span className="text-muted-foreground">{r.ms}ms</span>
                  </div>
                );
              })}
            </div>
            {msg.voting?.reason && (
              <div className="mt-2 text-[10px] text-muted-foreground italic">{msg.voting.reason}</div>
            )}
          </div>
        )}

        {!isUser && (
          <div className="mt-2 flex items-center gap-1 text-muted-foreground">
            <button className="text-[10px] font-mono hover:text-primary flex items-center gap-1">
              <RotateCcw className="w-2.5 h-2.5" /> Retry
            </button>
            <span className="opacity-30">·</span>
            <button className="text-[10px] font-mono hover:text-secondary flex items-center gap-1">
              <GitFork className="w-2.5 h-2.5" /> Fork
            </button>
            <span className="opacity-30">·</span>
            <button className="text-[10px] font-mono hover:text-accent flex items-center gap-1">
              <Zap className="w-2.5 h-2.5" /> Approve
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
